/**
 * The mobility API client — the SINGLE fetch surface every portal codes against.
 *
 * Every tmext_mobility endpoint returns the standard envelope
 * `{ success, data, meta, error }`. This client unwraps `data` on success and
 * throws a typed {@link ApiError} carrying the standard error `{ code, message }`
 * on failure — so callers work with domain data, not envelopes. GET/POST only
 * (banking policy).
 *
 * AUTH (M-1): unlike the cookie-session frontend/, mobility is a BEARER-token
 * SPA. Each request attaches `Authorization: Bearer <token>` from the
 * sessionStorage token store (lib/token-store.ts) + an `x-correlation-id`. A 401
 * CLEARS the token (session expired / role revoked / roster INACTIVE bites
 * within cache TTL) and throws a typed `UNAUTHORIZED` so the route guards
 * redirect to /login. Trust isolation is enforced SERVER-SIDE (repository
 * layer, entity-scoped) — this client never carries a role/entity claim.
 *
 * BASE_URL is '' (same origin): the SPA issues bare `/mobility/api/v1/...`
 * paths; the Vite dev proxy (and the prod nginx site block) forward `/mobility`
 * to the service on :3045.
 */

import { clearToken, getToken } from './token-store'
import { BASE_PATH } from './base-path'

/** Camel-case pagination block (repo convention, M-12) — NOT the spec's snake_case. */
export interface PaginationMeta {
  currentPage: number
  pageSize: number
  totalItems: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

/**
 * FLOW2 F-7: the snake_case list-summary block `GET /applications?mine=true`
 * rides under `meta.summary` (the M-12 camelCase lock covers ONLY the
 * pagination block — this one is snake_case by backend contract).
 */
export interface ListSummaryMeta {
  active_count: number
  closed_count: number
}

/**
 * WHAT THE SEARCH DID — present on `GET /jobs` only when the search index
 * answered. Absent means the SQL browse path ran, which is also the answer when
 * the query had no keyword.
 */
export interface JobSearchMeta {
  /** What the employee typed. */
  query: string
  /** What actually reached full-text search, after the rewrites below. */
  searchedText: string
  place?: {
    /** The gazetteer's canonical name — `Mumbai` for a typed `bombay`. */
    name: string
    /** The words that produced it — `in airoli`, or the location box value. */
    matchedFrom: string
    source: 'QUERY' | 'LOCATION_FILTER'
    /** Present when distance search is on; absent ⇒ exact-city filter. */
    radiusKm?: number
  }
  /**
   * The subsidiary/company the QUERY TEXT named exactly (no fuzzy matching,
   * no abbreviations yet). Absent when the query named no company, or when
   * an explicit channel/subsidiary filter already decided the narrowing.
   */
  entity?: {
    /** The entity's canonical name. */
    name: string
    /** The words that produced it — the phrase matched verbatim in the query. */
    matchedFrom: string
  }
  /** Filler dropped from the text (`jobs`, `roles`, `show me`). */
  droppedWords: string[]
  /** Engine filter expressions, verbatim — a debugging artefact, not display copy. */
  filters: string[]
  /**
   * The same narrowing IN WORDS, produced server-side by `explainJobFilters`
   * from the very input the predicates were built from. Optional for wire
   * compatibility with a service that predates it.
   */
  filtersExplained?: Array<{ label: string; detail: string }>
  /** Did this search ask for the vector leg (the Discover toggle)? */
  semanticRequested?: boolean
  /**
   * The master skills the query resolved to — "I read your words as these
   * skills". Empty with `semanticRequested: true` means the query named nothing
   * the taxonomy carries, so the search ran on text alone: a state worth saying
   * out loud, because the toggle was on and nothing appeared to change.
   */
  semanticSkills?: string[]
  leg: string | null
  /** Proposed BEFORE eligibility gating removed any. */
  candidates: number | null
  tookMs: number | null
}

export interface EnvelopeMeta {
  timestamp: string
  requestId: string
  version: string
  pagination?: PaginationMeta
  /** All visible board facets, included by GET /jobs for filter controls. */
  all_locations?: string[]
  all_subsidiaries?: Array<{ id: string; name: string }>
  all_departments?: string[]
  /** Present only on list endpoints that emit a summary (applications mine=true). */
  summary?: ListSummaryMeta
  /** Present only on GET /jobs, and only when the index answered. */
  search?: JobSearchMeta
  /** Present when employee eligibility could not be verified for a jobs list. */
  eligibility?: { status: 'UNAVAILABLE'; message: string }
}

interface SuccessEnvelope<T> {
  success: true
  data: T
  meta: EnvelopeMeta
  error: null
}

interface ErrorEnvelope {
  success: false
  data: null
  meta: EnvelopeMeta
  error: { code: string; message: string; details?: unknown[] }
}

type Envelope<T> = SuccessEnvelope<T> | ErrorEnvelope

/**
 * One item off the standard error envelope's `details[]` (D1-G6). The backend
 * normalizes every VALIDATION_ERROR detail to carry `{ field, message }`
 * additively (error-handler.ts) — extra library keys ride along untyped.
 */
export interface ApiErrorDetail {
  field?: string | null
  message?: string
  [key: string]: unknown
}

/** Keep only object-shaped detail items (the render contract tolerates junk). */
function toErrorDetails(details: unknown[] | undefined): ApiErrorDetail[] {
  if (!Array.isArray(details)) return []
  return details.filter(
    (d): d is ApiErrorDetail => d !== null && typeof d === 'object' && !Array.isArray(d),
  )
}

/** A typed error carrying the backend's standard error code (API_SPEC §12 taxonomy). */
export class ApiError extends Error {
  /** D1-G6: the envelope's `details[]` (field-level issues); empty when none rode. */
  public readonly details: ApiErrorDetail[]

  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    details: ApiErrorDetail[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
    this.details = details
  }
}

/** `{ data, meta }` — the success payload AND its meta (so a list reads `meta.pagination`). */
export interface PagedResponse<T> {
  data: T
  meta: EnvelopeMeta
}

// Same origin, but NOT necessarily the origin ROOT: under a subpath deploy the
// browser must ask for <base>/mobility/api/v1/... or the reverse proxy — which
// only matches the subpath — never forwards the call at all. '' for a root
// deploy, so this is a no-op there. The BACKEND still sees a bare
// /mobility/api/v1/... either way, because the proxy strips the prefix back
// off; nothing server-side knows about the mount path.
const BASE_URL = BASE_PATH

function correlationId(): string {
  const c = globalThis.crypto
  if (c && 'randomUUID' in c) return c.randomUUID()
  return `cid-${Math.random().toString(36).slice(2)}-${Date.now()}`
}

/** Build the auth + correlation headers, merging any caller-supplied ones. */
function baseHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'x-correlation-id': correlationId(),
    ...extra,
  }
  const token = getToken()
  if (token) headers['authorization'] = `Bearer ${token}`
  return headers
}

/** Turn a 401 into a signed-out state: clear the token and throw a typed UNAUTHORIZED. */
function unauthorized(): never {
  clearToken()
  throw new ApiError('UNAUTHORIZED', 'Your session has expired. Please sign in again.', 401)
}

export interface RequestOptions {
  /** Abort signal (component unmount / superseded request). */
  signal?: AbortSignal
  /** Extra headers merged over the defaults (never overrides the bearer token). */
  headers?: Record<string, string>
}

/**
 * The shared fetch core: issues the request, parses the standard envelope, and
 * either THROWS a typed {@link ApiError} (on `success:false` / a 401 / a
 * non-JSON body) or returns the parsed {@link SuccessEnvelope}. Every JSON path
 * composes this so 401 / error / correlation-id handling lives in one place.
 */
async function fetchEnvelope<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<SuccessEnvelope<T>> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: baseHeaders({ 'content-type': 'application/json', ...options?.headers }),
    // A POST always carries a JSON body (default `{}`) to match the content-type
    // header — a bodyless POST with that header makes Fastify 400 on empty parse.
    body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
    ...(options?.signal ? { signal: options.signal } : {}),
  })

  let envelope: Envelope<T> | null = null
  try {
    envelope = (await res.json()) as Envelope<T>
  } catch {
    if (res.status === 401) unauthorized()
    throw new ApiError('INTERNAL_ERROR', `Unexpected non-JSON response (${res.status}).`, res.status)
  }

  if (!envelope.success) {
    // A 401 signs us out (the token store is cleared) but PRESERVES the
    // backend's typed code — OTP_EXPIRED rides a 401 too, and the login page
    // switches on it. Non-JSON 401s (above) fall back to plain UNAUTHORIZED.
    if (res.status === 401) clearToken()
    throw new ApiError(
      envelope.error.code,
      envelope.error.message,
      res.status,
      toErrorDetails(envelope.error.details),
    )
  }
  return envelope
}

/** Issue a request and return ONLY the envelope's `data` (the common case). */
async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  return (await fetchEnvelope<T>(method, path, body, options)).data
}

/** Issue a request and return BOTH `data` and `meta` — the paging-aware variant. */
async function requestWithMeta<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<PagedResponse<T>> {
  const envelope = await fetchEnvelope<T>(method, path, body, options)
  return { data: envelope.data, meta: envelope.meta }
}

/**
 * POST/GET and read the response as a binary {@link Blob} — for the merged-PDF
 * export endpoint. A non-2xx returns the standard JSON error envelope, still
 * surfaced as a typed {@link ApiError}; a 401 clears the token + redirects.
 */
async function requestBlob(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<Blob> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: baseHeaders(
      method === 'POST' ? { 'content-type': 'application/json', ...options?.headers } : options?.headers,
    ),
    body: method === 'POST' ? JSON.stringify(body ?? {}) : undefined,
    ...(options?.signal ? { signal: options.signal } : {}),
  })

  if (res.ok && !res.headers.get('content-type')?.includes('application/json')) {
    return res.blob()
  }
  if (res.status === 401) clearToken()
  try {
    const envelope = (await res.json()) as Envelope<unknown>
    if (!envelope.success) {
      throw new ApiError(
        envelope.error.code,
        envelope.error.message,
        res.status,
        toErrorDetails(envelope.error.details),
      )
    }
  } catch (err) {
    if (err instanceof ApiError) throw err
  }
  if (res.status === 401) unauthorized()
  throw new ApiError('INTERNAL_ERROR', `Could not download the file (${res.status}).`, res.status)
}

/**
 * POST a `multipart/form-data` body (CSV / JD uploads) — NO JSON
 * content-type (the browser sets the multipart boundary). Still unwraps the
 * standard JSON envelope + 401 handling.
 */
async function postMultipart<T>(
  path: string,
  form: FormData,
  options?: RequestOptions,
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    // NOTE: do NOT set content-type — the browser adds the multipart boundary.
    headers: baseHeaders(options?.headers),
    body: form,
    ...(options?.signal ? { signal: options.signal } : {}),
  })

  let envelope: Envelope<T> | null = null
  try {
    envelope = (await res.json()) as Envelope<T>
  } catch {
    if (res.status === 401) unauthorized()
    throw new ApiError('INTERNAL_ERROR', `Unexpected non-JSON response (${res.status}).`, res.status)
  }
  if (!envelope.success) {
    // Same 401 posture as fetchEnvelope: sign out, keep the typed code.
    if (res.status === 401) clearToken()
    throw new ApiError(
      envelope.error.code,
      envelope.error.message,
      res.status,
      toErrorDetails(envelope.error.details),
    )
  }
  return envelope.data
}

/** The low-level client (absolute `/mobility/...` paths). Portals use {@link DomainClient}. */
export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, body, options),
  getPage: <T>(path: string, options?: RequestOptions): Promise<PagedResponse<T>> =>
    requestWithMeta<T>('GET', path, undefined, options),
  postPage: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<PagedResponse<T>> =>
    requestWithMeta<T>('POST', path, body, options),
  getBlob: (path: string, options?: RequestOptions): Promise<Blob> => requestBlob('GET', path, undefined, options),
  postBlob: (path: string, body?: unknown, options?: RequestOptions): Promise<Blob> =>
    requestBlob('POST', path, body, options),
  postMultipart: <T>(path: string, form: FormData, options?: RequestOptions): Promise<T> =>
    postMultipart<T>(path, form, options),
}

/**
 * A client bound to ONE service prefix, so callers issue short relative paths
 * (`domain.get('/jobs')`) instead of repeating `/mobility/api/v1`.
 */
export interface DomainClient {
  readonly prefix: string
  get: <T>(path: string, options?: RequestOptions) => Promise<T>
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  getPage: <T>(path: string, options?: RequestOptions) => Promise<PagedResponse<T>>
  postPage: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<PagedResponse<T>>
  getBlob: (path: string, options?: RequestOptions) => Promise<Blob>
  postBlob: (path: string, body?: unknown, options?: RequestOptions) => Promise<Blob>
  postMultipart: <T>(path: string, form: FormData, options?: RequestOptions) => Promise<T>
}

function domain(prefix: string): DomainClient {
  return {
    prefix,
    get: (path, options) => api.get(`${prefix}${path}`, options),
    post: (path, body, options) => api.post(`${prefix}${path}`, body, options),
    getPage: (path, options) => api.getPage(`${prefix}${path}`, options),
    postPage: (path, body, options) => api.postPage(`${prefix}${path}`, body, options),
    getBlob: (path, options) => api.getBlob(`${prefix}${path}`, options),
    postBlob: (path, body, options) => api.postBlob(`${prefix}${path}`, body, options),
    postMultipart: (path, form, options) => api.postMultipart(`${prefix}${path}`, form, options),
  }
}

/** The mobility domain client — bound to the service's `/mobility/api/v1` base. */
export const mobilityClient: DomainClient = domain('/mobility/api/v1')
